import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { IoMdEye } from "react-icons/io";
import { MdFilterAlt } from "react-icons/md";
import { CiSearch } from "react-icons/ci";
import {
  getAllChats,
  createMessage,
  getMessages,
  markMessageAsRead,
} from "../../../api/common/chat";
import { BiSolidBot } from "react-icons/bi";
import { Link } from "react-router-dom";
import CustomerSupportIcon from "../../../helper/images/customer.png";
import { useAuth } from "../../../context/AuthContext";
import UserImage from "../../../helper/images/customer.png";
import socket from "../../../helper/socket";
import SupportChat from "../../../Layout/Chat";
import Queries from "../../../Layout/Queries";
import { useTranslation } from "../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const CustomerSupport = () => {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [status, setStatus] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const statuses = [
    t("Support").statuses.inProgress,
    t("Support").statuses.pending,
    t("Support").statuses.resolved,
  ];
  const [inputMessage, setInputMessage] = useState("");
  const queryClient = useQueryClient();
  const { userData } = useAuth();
  const debouncedMarkAsReadRef = useRef();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["allChats", debouncedSearch],
    queryFn: () => getAllChats(debouncedSearch),
    keepPreviousData: true,
  });

  const { data: messagesData, isLoading: isChatLoading } = useQuery({
    queryKey: ["all-messages", selectedChat?._id],
    queryFn: () => getMessages(selectedChat._id),
    enabled: !!selectedChat?._id,
  });

  // Map status filter to determine chat status
  const getChatStatus = (chat) => {
    // Pending: has unread messages
    if (chat.unreadCount > 0) {
      return t("Support").statuses.pending;
    }
    
    // Check if chat was updated recently (within last 7 days) to determine if it's in progress
    const updatedAt = new Date(chat.updatedAt);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    // In Progress: no unread messages but recently updated
    if (updatedAt > sevenDaysAgo) {
      return t("Support").statuses.inProgress;
    }
    
    // Resolved: no unread messages and not recently updated
    return t("Support").statuses.resolved;
  };

  // Filter chats by status if status filter is applied
  const filterChatsByStatus = (chats) => {
    if (!status || !chats) return chats;
    
    return chats.filter((chat) => {
      const chatStatus = getChatStatus(chat);
      return chatStatus === status;
    });
  };

  const allChats = data?.data || [];
  const chats = filterChatsByStatus(allChats);
  const messages = messagesData?.data?.messages;

  const messageMutation = useMutation({
    mutationFn: createMessage,
    onSuccess: () => {
      queryClient.invalidateQueries(["allChats"]);
      setInputMessage("");
    },
  });

  const messageReadMutation = useMutation({
    mutationFn: markMessageAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries(["allChats"]);
      setInputMessage("");
    },
  });

  useEffect(() => {
    if (selectedChat?._id) {
      socket.emit("joinChat", selectedChat._id);
      messageReadMutation.mutate(selectedChat?._id);
    }

    return () => {
      if (selectedChat?._id) {
        socket.emit("leaveRoom", selectedChat._id);
      }
    };
  }, [selectedChat?._id]);

  //  useEffect(() => {
  //   const handleReceiveMessage = (newMessage) => {
  //     if (newMessage.chat === selectedChat?._id) {
  //       queryClient.invalidateQueries(["all-messages", selectedChat._id]);
  //     }
  //   };

  //   socket.on("receiveMessage", handleReceiveMessage);

  //   return () => {
  //     socket.off("receiveMessage", handleReceiveMessage);
  //   };
  // }, [selectedChat?._id]);

  const handleFilterChange = (value) => {
    setStatus(value);
    setIsOpen(false);
  };

  const handleView = (chat) => {
    setSelectedChat(chat);
    setIsPopupOpen(true);
  };

  const totalParticipants = selectedChat?.participants?.length || 1;

  useEffect(() => {
    const handleReceiveMessage = (newMessage) => {
      if (newMessage.chat === selectedChat?._id) {
        queryClient.invalidateQueries(["all-messages", selectedChat._id]);
        queryClient.invalidateQueries(["allChats"]);

        if (debouncedMarkAsReadRef.current) {
          debouncedMarkAsReadRef.current(selectedChat._id);
        }
      }
    };

    const handleMessageRead = ({ chatId, userId }) => {
      if (chatId === selectedChat?._id) {
        queryClient.setQueryData(["all-messages", chatId], (oldData) => {
          if (!oldData?.data?.messages) return oldData;

          const updatedMessages = oldData.data.messages.map((msg) => {
            if (!msg.readBy?.includes(userId)) {
              return {
                ...msg,
                readBy: [...msg.readBy, userId],
              };
            }
            return msg;
          });

          return {
            ...oldData,
            data: {
              ...oldData.data,
              messages: updatedMessages,
            },
          };
        });
      }
    };

    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("messageRead", handleMessageRead);

    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("messageRead", handleMessageRead);
    };
  }, [selectedChat?._id]);

  useEffect(() => {
    const handleReceiveMessage = (newMessage) => {
      queryClient.invalidateQueries(["allChats"]);
    };
    socket.on("receiveMessage", handleReceiveMessage);
    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
    };
  }, []);

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  useEffect(() => {
    debouncedMarkAsReadRef.current = debounce((chatId) => {
      messageReadMutation.mutate(chatId);
    }, 500);

    return () => {
      debouncedMarkAsReadRef.current = null;
    };
  }, []);

  return (
    <>
      <div className="flex justify-between items-center flex-wrap gap-[20px]">
        <h2 className="text-[22px] font-medium">
          {t("Support").customerSupport}
        </h2>
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="py-[9px] px-[35px] bg-white border border-[#DDDDDD] flex items-center gap-2 rounded-[2px]"
          >
            <MdFilterAlt /> {status || t("Support").filter}
          </button>
          {isOpen && (
            <div className="absolute mt-2 w-36 bg-white border rounded shadow text-sm font-medium z-10">
              {statuses?.map((item) => (
                <div
                  key={item}
                  onClick={() => handleFilterChange(item)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Queries chats={chats} isLoading={isLoading} handleView={handleView} search={search} setSearch={setSearch} />

      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <SupportChat
          selectedChat={selectedChat}
          messages={messages}
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          messageMutation={messageMutation}
          userData={userData}
          totalParticipants={totalParticipants}
        />
      </PopupModal>
    </>
  );
};

export default CustomerSupport;
